import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import {
  Sparkles, ShieldCheck, CalendarClock, Ban, RotateCcw, Receipt, ArrowRight, Zap,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function fmtMxn(n) {
  return "$" + Number(n || 0).toLocaleString("es-MX") + " MXN";
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function AlumnoSuscripcion() {
  const { user } = useAuth();
  const { subscription, hasAllAccess, loading, refresh } = useSubscription();
  const [invoices, setInvoices] = useState([]);
  const [acting, setActing] = useState(false);
  const [params] = useSearchParams();
  const showWelcome = params.get("welcome") === "1";

  useEffect(() => {
    api.get("/subscriptions/me/payments?limit=12")
      .then((r) => setInvoices(r.data.items || []))
      .catch(() => setInvoices([]));
  }, [subscription?.id]);

  const cancel = async () => {
    setActing(true);
    try {
      const r = await api.post("/subscriptions/cancel");
      toast.success(r.data.message || "Cancelación programada");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  const resume = async () => {
    setActing(true);
    try {
      const r = await api.post("/subscriptions/resume");
      toast.success(r.data.message || "Suscripción reactivada");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  return (
    <AlumnoLayout user={user}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">
          Suscripción
        </p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">
          Mi All-Access
        </h1>
        <p className="mt-2 text-gray-400">
          Gestiona tu plan, renovación y pagos.
        </p>

        {showWelcome && hasAllAccess && (
          <div
            className="mt-6 p-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 flex items-start gap-3"
            data-testid="welcome-banner"
          >
            <Sparkles className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-100">
                ¡Bienvenido a All-Access!
              </p>
              <p className="text-xs text-emerald-200">
                Ya puedes iniciar cualquier programa publicado del catálogo.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-8 h-64 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />
        ) : !subscription ? (
          <EmptyState />
        ) : (
          <>
            <ActiveSubCard
              sub={subscription}
              acting={acting}
              onCancel={cancel}
              onResume={resume}
            />

            <section className="mt-10" data-testid="invoices-section">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5" /> Historial de pagos
              </h2>
              {invoices.length === 0 ? (
                <p className="text-sm text-gray-500">Aún no hay pagos registrados.</p>
              ) : (
                <div className="overflow-x-auto border border-gray-800 rounded-xl">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
                      <tr>
                        <th className="text-left px-4 py-3">Folio</th>
                        <th className="text-left px-4 py-3">Plan</th>
                        <th className="text-left px-4 py-3">Periodo</th>
                        <th className="text-right px-4 py-3">Monto</th>
                        <th className="text-left px-4 py-3">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-t border-gray-800" data-testid={`invoice-row-${inv.id}`}>
                          <td className="px-4 py-3 font-mono text-xs text-white">{inv.receipt_number}</td>
                          <td className="px-4 py-3 text-gray-200 capitalize">{(inv.plan_slug || "").replace(/-/g, " ")}</td>
                          <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                            {fmtDate(inv.period_start)} → {fmtDate(inv.period_end)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-white">
                            {fmtMxn(inv.amount_mxn)}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[10px] px-2 py-0.5 rounded uppercase font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                              {inv.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </AlumnoLayout>
  );
}

function ActiveSubCard({ sub, acting, onCancel, onResume }) {
  const isActive = sub.is_all_access_active;
  const statusStyles = {
    active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    trialing: "bg-brand-orange-500/10 text-brand-orange-300 border-primary/30",
    past_due: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    canceled: "bg-gray-500/10 text-gray-300 border-gray-500/30",
    expired: "bg-red-500/10 text-red-300 border-red-500/30",
  };
  const statusLabel = {
    active: "Activa",
    trialing: "En prueba",
    past_due: "Con pago pendiente",
    canceled: "Cancelada",
    expired: "Expirada",
  };
  return (
    <section
      className="mt-8 p-6 rounded-2xl border border-gray-800 bg-gradient-to-br from-[#111827] via-[#0F172A] to-[#0B0F14] relative overflow-hidden"
      data-testid="active-subscription-card"
    >
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">
              Plan actual
            </p>
            <h2 className="mt-1 text-2xl font-bold text-white flex items-center gap-2">
              {sub.plan_name || "All-Access"}
              {isActive && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/20 border border-primary/50 text-[10px] font-bold uppercase tracking-widest text-[#93C5FD]">
                  <ShieldCheck className="w-3 h-3" /> All-Access
                </span>
              )}
            </h2>
            <p className="mt-1 text-sm text-gray-400">
              {sub.billing_cycle === "annual" ? "Anual" : "Mensual"} ·{" "}
              <span className="font-mono">{fmtMxn(sub.price_mxn)}</span>
            </p>
          </div>
          <span
            className={`text-[11px] px-3 py-1 rounded-full border font-bold uppercase tracking-widest ${statusStyles[sub.status] || "bg-gray-500/10 text-gray-300 border-gray-500/30"}`}
            data-testid="sub-status-chip"
          >
            {statusLabel[sub.status] || sub.status}
          </span>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          <InfoTile
            icon={CalendarClock}
            label="Inició"
            value={fmtDate(sub.started_at)}
          />
          <InfoTile
            icon={CalendarClock}
            label={sub.cancel_at_period_end || !isActive ? "Termina" : "Se renueva"}
            value={fmtDate(sub.current_period_end)}
          />
          <InfoTile
            icon={Sparkles}
            label="Próximo cargo"
            value={sub.next_billing_at ? fmtDate(sub.next_billing_at) : "—"}
          />
        </div>

        {sub.cancel_at_period_end && isActive && (
          <div
            className="mt-6 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-200 text-sm flex items-start gap-2"
            data-testid="cancel-scheduled-banner"
          >
            <Ban className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">Cancelación programada</p>
              <p className="text-xs">
                Tu acceso terminará el {fmtDate(sub.current_period_end)}. Puedes reactivarla antes.
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {isActive && !sub.cancel_at_period_end && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  disabled={acting}
                  data-testid="cancel-sub-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-700 text-gray-200 hover:bg-white/5 text-sm font-semibold disabled:opacity-60"
                >
                  <Ban className="w-4 h-4" /> Cancelar al final del periodo
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#111827] border-gray-800 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Cancelar tu suscripción?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-400">
                    Mantendrás acceso hasta el {fmtDate(sub.current_period_end)}. No se realizará el
                    siguiente cargo. Puedes reactivarla en cualquier momento antes del vencimiento.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-200">
                    Volver
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onCancel}
                    data-testid="confirm-cancel-sub-btn"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Sí, cancelar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {isActive && sub.cancel_at_period_end && (
            <button
              type="button"
              onClick={onResume}
              disabled={acting}
              data-testid="resume-sub-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold disabled:opacity-60"
            >
              <RotateCcw className="w-4 h-4" /> Reactivar suscripción
            </button>
          )}
          <Link
            to="/catalogo"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-700 text-gray-200 hover:bg-white/5 text-sm font-semibold"
            data-testid="explore-catalog-cta"
          >
            Explorar catálogo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="p-3 bg-[#0F172A]/60 border border-gray-800 rounded-lg">
      <p className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </p>
      <p className="mt-1 font-mono text-sm text-white">{value}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <section
      className="mt-8 p-8 rounded-2xl border border-gray-800 bg-[#111827] text-center"
      data-testid="no-subscription-cta"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-[#B84A0C]/20 border border-primary/40 flex items-center justify-center">
        <Sparkles className="w-6 h-6 text-[#93C5FD]" />
      </div>
      <h2 className="mt-4 text-xl font-bold text-white">Aún no tienes All-Access</h2>
      <p className="mt-2 text-sm text-gray-400 max-w-md mx-auto">
        Con un plan All-Access desbloqueas acceso ilimitado a los 195+ programas del catálogo mientras tu suscripción esté vigente.
      </p>
      <div className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-widest">
        <Zap className="w-3 h-3" /> Pago mock · sin cargo real
      </div>
      <div className="mt-6">
        <Link
          to="/planes"
          data-testid="view-plans-cta"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-bold text-sm transition-colors"
        >
          Ver planes All-Access <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
